import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  obtenerAsistenciaPromocion,
  obtenerAsistenciaDocentesPromocion,
  justificarAsistenciaPromocion,
  eliminarAsistenciaPromocion,
  listarBloquesSesionPromocion,
  obtenerAsistenciaBloquePromocion,
  justificarAsistenciaBloquePromocion,
  eliminarAsistenciaBloquePromocion,
} from "../../../../services/promocionesService";
import { showToast } from "../../../../utils/showToast";
import { usePermissions } from "../../../../utils/permissions";
import AsistenciaSesionModal from "./AsistenciaSesionModal";
import AsistenciaBloqueModal from "./AsistenciaBloqueModal";
import AsistenciaDocentesTable from "./AsistenciaDocentesTable";

// Chooser al hacer clic en el día: marcar el día completo (lo de siempre) o
// entrar al detalle por materia (independiente, no afecta el % oficial).
function ElegirModoAsistenciaModal({ sesion, onDiaCompleto, onPorMateria, onCerrar }) {
  if (!sesion) return null;
  const [y, m, d] = String(sesion.fecha).split("-");
  return (
    <div className="modal d-block" style={{ background: "rgba(0,0,0,.5)" }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h6 className="modal-title fw-bold">
              <i className="bi bi-calendar-check me-2" />
              Asistencia — {d}/{m}/{y}
            </h6>
            <button type="button" className="btn-close" onClick={onCerrar} />
          </div>
          <div className="modal-body d-flex flex-column gap-2">
            <button type="button" className="btn btn-outline-primary text-start py-3" onClick={onDiaCompleto}>
              <i className="bi bi-calendar2-check me-2" />
              <strong>Marcar día completo</strong>
              <div className="text-muted small mt-1">Un solo estado para todo el día (lo de siempre).</div>
            </button>
            <button type="button" className="btn btn-outline-secondary text-start py-3" onClick={onPorMateria}>
              <i className="bi bi-journal-bookmark me-2" />
              <strong>Marcar por materia</strong>
              <div className="text-muted small mt-1">Detalle fino por cada materia del día — no afecta el % oficial.</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Lista de materias (bloques) del día elegido, para entrar al detalle de cada una.
function BloquesSesionModal({ sesion, bloques, loading, onElegirBloque, onCerrar }) {
  if (!sesion) return null;
  return (
    <div className="modal d-block" style={{ background: "rgba(0,0,0,.5)" }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h6 className="modal-title fw-bold">
              <i className="bi bi-journal-bookmark me-2" />
              Materias de este día
            </h6>
            <button type="button" className="btn-close" onClick={onCerrar} />
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center text-muted py-3">
                <span className="spinner-border spinner-border-sm me-2" />
                Cargando materias...
              </div>
            ) : !bloques.length ? (
              <p className="text-muted text-center mb-0 py-3">Este día no tiene materias (clases) programadas.</p>
            ) : (
              <div className="list-group">
                {bloques.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    onClick={() => onElegirBloque(b)}
                  >
                    <span>
                      <strong>{b.materia_nombre || "Sin materia"}</strong>
                      <div className="text-muted small">{b.docente_nombre || "Sin docente"}</div>
                    </span>
                    <span className="text-muted small">
                      {b.hora_inicio?.slice(0, 5)}–{b.hora_fin?.slice(0, 5)}
                      {b.modalidad === "online" && <><br /><span className="badge bg-info-subtle text-info mt-1">Virtual</span></>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const VENTANA = 7; // sesiones visibles a la vez — cómodo tanto en celular como en desktop

const ESTADO_INFO = {
  presente: { label: "Presente", icon: "bi-check-circle-fill", clase: "text-success" },
  virtual: { label: "Presente (clase virtual)", icon: "bi-camera-video-fill", clase: "text-info" },
  ausente: { label: "Ausente", icon: "bi-x-circle-fill", clase: "text-danger" },
  justificado: { label: "Justificado", icon: "bi-shield-fill-check", clase: "text-warning" },
  visto: { label: "Visto (online)", icon: "bi-camera-video-fill", clase: "text-info" },
  pendiente: { label: "Pendiente", icon: "bi-dash-circle", clase: "text-muted" },
};

const ASISTE = ["presente", "justificado", "visto", "virtual"];

const formatFechaCorta = (fecha) => {
  if (!fecha) return "";
  const [, m, d] = String(fecha).split("-");
  return `${d}/${m}`;
};
const formatFecha = (fecha) => {
  if (!fecha) return "";
  const [y, m, d] = String(fecha).split("-");
  return `${d}/${m}/${y}`;
};

export default function PromocionAsistenciaTab({ promocionId }) {
  const { hasPermission } = usePermissions();
  const puedeJustificar = hasPermission("promociones.asistencia.justificar");

  const [vista, setVista] = useState("estudiantes"); // "estudiantes" | "docentes"

  const [sesiones, setSesiones] = useState([]);
  const [estudiantes, setEstudiantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inicioVentana, setInicioVentana] = useState(0);
  const [modal, setModal] = useState(null); // { sesion, focusEstudianteId } | null — día completo
  const [chooser, setChooser] = useState(null); // { sesion, focusEstudianteId } | null
  const [bloquesSesion, setBloquesSesion] = useState(null); // { sesion, lista, loading } | null
  const [modalBloque, setModalBloque] = useState(null); // { bloque, estudiantes } | null

  const [docentes, setDocentes] = useState([]);
  const [docentesLoading, setDocentesLoading] = useState(false);
  const [docentesError, setDocentesError] = useState("");
  const [docentesCargados, setDocentesCargados] = useState(false);

  const cargar = useCallback(async () => {
    if (!promocionId) return;
    setLoading(true);
    setError("");
    try {
      const data = await obtenerAsistenciaPromocion(promocionId);
      setSesiones(data.sesiones || []);
      setEstudiantes(data.estudiantes || []);
      setInicioVentana(Math.max(0, (data.sesiones || []).length - VENTANA));
    } catch (err) {
      setError(err.message || "No se pudo cargar la asistencia.");
    } finally {
      setLoading(false);
    }
  }, [promocionId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cargarDocentes = useCallback(async () => {
    if (!promocionId) return;
    setDocentesLoading(true);
    setDocentesError("");
    try {
      const data = await obtenerAsistenciaDocentesPromocion(promocionId);
      setDocentes(data.docentes || []);
      setDocentesCargados(true);
    } catch (err) {
      setDocentesError(err.message || "No se pudo cargar la asistencia de docentes.");
    } finally {
      setDocentesLoading(false);
    }
  }, [promocionId]);

  useEffect(() => {
    if (vista === "docentes" && !docentesCargados) {
      cargarDocentes();
    }
  }, [vista, docentesCargados, cargarDocentes]);

  const sesionesVisibles = useMemo(
    () => sesiones.slice(inicioVentana, inicioVentana + VENTANA),
    [sesiones, inicioVentana]
  );

  const handleGuardar = async (estudianteId, estado, observacion) => {
    try {
      await justificarAsistenciaPromocion(promocionId, modal.sesion.id, estudianteId, {
        estado,
        observacion,
      });
      // Actualiza en memoria sin recargar todo — misma UX que el resto de la app
      setEstudiantes((prev) =>
        prev.map((est) =>
          est.estudiante_id === estudianteId
            ? {
                ...est,
                celdas: {
                  ...est.celdas,
                  [modal.sesion.id]: { estado, origen: "manual", observacion: observacion || null },
                },
              }
            : est
        )
      );
      showToast({ type: "success", message: "Asistencia actualizada." });
      // Recalcula el % del estudiante afectado igual que lo hace el backend
      recalcularResumen(estudianteId);
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo actualizar la asistencia." });
    }
  };

  const handleEliminar = async (estudianteId) => {
    try {
      await eliminarAsistenciaPromocion(promocionId, modal.sesion.id, estudianteId);
      setEstudiantes((prev) =>
        prev.map((est) => {
          if (est.estudiante_id !== estudianteId) return est;
          const celdas = { ...est.celdas };
          delete celdas[modal.sesion.id];
          return { ...est, celdas };
        })
      );
      showToast({ type: "success", message: "Marca de asistencia eliminada." });
      recalcularResumen(estudianteId);
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo eliminar la asistencia." });
    }
  };

  const recalcularResumen = (estudianteId) => {
    setEstudiantes((prev) =>
      prev.map((est) => {
        if (est.estudiante_id !== estudianteId) return est;
        const valores = Object.values(est.celdas);
        const conRegistro = valores.filter((v) => v.origen !== "sin_registro");
        const asistidas = conRegistro.filter((v) => ASISTE.includes(v.estado));
        const porcentaje = conRegistro.length ? Math.round((asistidas.length / conRegistro.length) * 100) : null;
        return {
          ...est,
          resumen: { ...est.resumen, total_con_registro: conRegistro.length, asistidas: asistidas.length, porcentaje_asistencia: porcentaje },
        };
      })
    );
  };

  // Ahora el clic abre el chooser (día completo vs por materia) en vez de ir
  // directo al modal — "por materia" es un detalle aparte, no reemplaza esto.
  const abrirSesion = (sesion, focusEstudianteId = null) => setChooser({ sesion, focusEstudianteId });

  const elegirDiaCompleto = () => {
    setModal(chooser);
    setChooser(null);
  };

  const elegirPorMateria = async () => {
    const sesion = chooser?.sesion;
    setChooser(null);
    setBloquesSesion({ sesion, lista: [], loading: true });
    try {
      const data = await listarBloquesSesionPromocion(promocionId, sesion.id);
      setBloquesSesion({ sesion, lista: data.bloques || [], loading: false });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudieron cargar las materias del día." });
      setBloquesSesion(null);
    }
  };

  const elegirBloque = async (bloque) => {
    try {
      const data = await obtenerAsistenciaBloquePromocion(promocionId, bloque.id);
      setModalBloque({ bloque, estudiantes: data.estudiantes || [] });
      setBloquesSesion(null);
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo cargar la asistencia de esta materia." });
    }
  };

  const handleGuardarBloque = async (estudianteId, estado, observacion) => {
    try {
      await justificarAsistenciaBloquePromocion(promocionId, modalBloque.bloque.id, estudianteId, { estado, observacion });
      setModalBloque((prev) => ({
        ...prev,
        estudiantes: prev.estudiantes.map((est) =>
          est.estudiante_id === estudianteId
            ? { ...est, estado, origen: "manual", observacion: observacion || null }
            : est
        ),
      }));
      showToast({ type: "success", message: "Asistencia por materia actualizada." });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo actualizar la asistencia." });
    }
  };

  const handleEliminarBloque = async (estudianteId) => {
    try {
      await eliminarAsistenciaBloquePromocion(promocionId, modalBloque.bloque.id, estudianteId);
      setModalBloque((prev) => ({
        ...prev,
        estudiantes: prev.estudiantes.map((est) =>
          est.estudiante_id === estudianteId
            ? { ...est, estado: "pendiente", origen: "sin_registro", observacion: null }
            : est
        ),
      }));
      showToast({ type: "success", message: "Marca de asistencia eliminada." });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo eliminar la asistencia." });
    }
  };

  const porcentajeClase = (pct) => {
    if (pct === null || pct === undefined) return "text-muted";
    if (pct >= 80) return "text-success fw-semibold";
    if (pct >= 60) return "text-warning fw-semibold";
    return "text-danger fw-semibold";
  };

  // Igual que "Listado": # y Matrícula antes del nombre. Las tres quedan fijas
  // (sticky) juntas al hacer scroll horizontal entre sesiones.
  const COL_NUM_W = 44;
  const COL_MATRICULA_W = 110;
  // Mismo celeste bajito que ya usa el badge de modalidad online en "Listado"
  // (bg-info-subtle de Bootstrap) — se repite acá para que la fila completa
  // se distinga de un vistazo entre presencial y online.
  const BG_ONLINE = "var(--bs-info-bg-subtle, #cff4fc)";
  const BG_DEFAULT = "var(--bs-body-bg, #fff)";
  const stickyAt = (left, online = false) => ({
    position: "sticky",
    left,
    zIndex: 2,
    background: online ? BG_ONLINE : BG_DEFAULT,
  });
  const stickyRight = (online = false) => ({
    position: "sticky",
    right: 0,
    zIndex: 2,
    background: online ? BG_ONLINE : BG_DEFAULT,
  });

  return (
    <div className="management-card promociones-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
        <div>
          <p className="management-label mb-1">Asistencia</p>
          <h5 className="mb-0">
            {vista === "estudiantes" ? "Reporte de asistencia — presencial y online" : "Asistencia de docentes"}
          </h5>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${vista === "estudiantes" ? "btn-secondary" : "btn-outline-secondary"}`}
              onClick={() => setVista("estudiantes")}
            >
              <i className="bi bi-people me-1" />Estudiantes
            </button>
            <button
              type="button"
              className={`btn ${vista === "docentes" ? "btn-secondary" : "btn-outline-secondary"}`}
              onClick={() => setVista("docentes")}
            >
              <i className="bi bi-person-workspace me-1" />Docentes
            </button>
          </div>
          {vista === "estudiantes" && (
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={inicioVentana === 0}
                onClick={() => setInicioVentana((v) => Math.max(0, v - VENTANA))}
              >
                <i className="bi bi-chevron-left" />
              </button>
              <span className="text-muted" style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                {sesiones.length ? `${inicioVentana + 1}–${Math.min(inicioVentana + VENTANA, sesiones.length)} de ${sesiones.length} sesiones` : "Sin sesiones"}
              </span>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={inicioVentana + VENTANA >= sesiones.length}
                onClick={() => setInicioVentana((v) => Math.min(sesiones.length - VENTANA, v + VENTANA))}
              >
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          )}
        </div>
      </div>

      {vista === "docentes" ? (
        <AsistenciaDocentesTable
          docentes={docentes}
          loading={docentesLoading}
          error={docentesError}
          onReintentar={cargarDocentes}
        />
      ) : (
        <>
          {/* Leyenda */}
          <div className="d-flex flex-wrap gap-3 mb-3" style={{ fontSize: "0.8rem" }}>
            {Object.entries(ESTADO_INFO).map(([key, info]) => (
              <span key={key} className={info.clase}>
                <i className={`bi ${info.icon} me-1`} />
                {info.label}
              </span>
            ))}
          </div>

          {error && (
            <div className="alert alert-warning d-flex align-items-center justify-content-between management-alert mb-3">
              <div>{error}</div>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cargar}>
                Reintentar
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center text-muted py-4">
              <span className="spinner-border spinner-border-sm me-2" />
              Cargando asistencia...
            </div>
          ) : estudiantes.length === 0 ? (
            <p className="text-muted text-center py-4 mb-0">No hay estudiantes inscritos o sesiones registradas todavía.</p>
          ) : (
            <div className="table-responsive" style={{ maxWidth: "100%" }}>
              <table className="table table-sm table-hover align-middle mb-0" style={{ minWidth: 640 }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ ...stickyAt(0), minWidth: COL_NUM_W }}>#</th>
                    <th style={{ ...stickyAt(COL_NUM_W), minWidth: COL_MATRICULA_W }}>Matrícula</th>
                    <th style={{ ...stickyAt(COL_NUM_W + COL_MATRICULA_W), minWidth: 180 }}>Estudiante</th>
                    {sesionesVisibles.map((s) => (
                      <th
                        key={s.id}
                        className="text-center"
                        style={{ cursor: "pointer", minWidth: 60 }}
                        title={`Ver/editar asistencia del ${formatFecha(s.fecha)}`}
                        onClick={() => abrirSesion(s)}
                      >
                        {formatFechaCorta(s.fecha)}
                        <i className="bi bi-pencil-square ms-1 text-muted" style={{ fontSize: "0.7rem" }} />
                      </th>
                    ))}
                    <th className="text-center" style={{ ...stickyRight(), minWidth: 70 }}>
                      Final
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {estudiantes.map((est, idx) => {
                    const esOnline = est.modalidad === "online";
                    return (
                    <tr key={est.estudiante_id} className={esOnline ? "table-info" : ""}>
                      <td style={stickyAt(0, esOnline)}>{idx + 1}</td>
                      <td style={stickyAt(COL_NUM_W, esOnline)}>{est.codigo_matricula || "-"}</td>
                      <td style={stickyAt(COL_NUM_W + COL_MATRICULA_W, esOnline)}>
                        <div className="fw-semibold">{est.nombre_completo}</div>
                        <div className={esOnline ? "text-info-emphasis" : "text-muted"} style={{ fontSize: "0.72rem" }}>
                          {esOnline ? (
                            <span className="fw-semibold"><i className="bi bi-camera-video-fill me-1" />Online</span>
                          ) : (
                            "Presencial"
                          )}
                        </div>
                      </td>
                      {sesionesVisibles.map((s) => {
                        const celda = est.celdas[s.id] || { estado: "pendiente" };
                        const info = ESTADO_INFO[celda.estado] || ESTADO_INFO.pendiente;
                        return (
                          <td
                            key={s.id}
                            className={`text-center ${info.clase}`}
                            style={{ cursor: "pointer" }}
                            title={info.label}
                            onClick={() => abrirSesion(s, est.estudiante_id)}
                          >
                            <i className={`bi ${info.icon}`} />
                          </td>
                        );
                      })}
                      <td className={`text-center ${porcentajeClase(est.resumen?.porcentaje_asistencia)}`} style={stickyRight(esOnline)}>
                        {est.resumen?.porcentaje_asistencia ?? "—"}
                        {est.resumen?.porcentaje_asistencia !== null && est.resumen?.porcentaje_asistencia !== undefined ? "%" : ""}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {modal && (
        <AsistenciaSesionModal
          sesion={modal.sesion}
          estudiantes={estudiantes}
          puedeEditar={puedeJustificar}
          focusEstudianteId={modal.focusEstudianteId}
          onGuardar={handleGuardar}
          onEliminar={handleEliminar}
          onCerrar={() => setModal(null)}
        />
      )}

      {chooser && (
        <ElegirModoAsistenciaModal
          sesion={chooser.sesion}
          onDiaCompleto={elegirDiaCompleto}
          onPorMateria={elegirPorMateria}
          onCerrar={() => setChooser(null)}
        />
      )}

      {bloquesSesion && (
        <BloquesSesionModal
          sesion={bloquesSesion.sesion}
          bloques={bloquesSesion.lista}
          loading={bloquesSesion.loading}
          onElegirBloque={elegirBloque}
          onCerrar={() => setBloquesSesion(null)}
        />
      )}

      {modalBloque && (
        <AsistenciaBloqueModal
          bloque={modalBloque.bloque}
          estudiantes={modalBloque.estudiantes}
          puedeEditar={puedeJustificar}
          onGuardar={handleGuardarBloque}
          onEliminar={handleEliminarBloque}
          onCerrar={() => setModalBloque(null)}
        />
      )}
    </div>
  );
}
